import { buildCustomerSummaries, deriveCustomerStatus, formatMonthLabel } from './customers';
import type {
  AttendanceSession,
  HomeDashboardData,
  HomeStaffPerformanceRecord,
  ServiceCatalogEntry,
  StaffMember,
  VisitorEntry,
} from './types';

const SERVICE_PALETTE = ['#27272A', '#52525B', '#71717A', '#A1A1AA', '#3F3F46', '#D4D4D8'];

function startOfDay(date: Date) {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

function isSameDay(left: Date, right: Date) {
  return (
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate()
  );
}

function isSameMonth(left: Date, right: Date) {
  return left.getFullYear() === right.getFullYear() && left.getMonth() === right.getMonth();
}

function formatWeekDay(date: Date) {
  return new Intl.DateTimeFormat('en-US', { weekday: 'short' }).format(date);
}

function formatTime(date: Date) {
  return new Intl.DateTimeFormat('en-IN', {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function average(values: number[]) {
  if (values.length === 0) {
    return 0;
  }

  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function buildWeeklyTrend(referenceDate: Date, visitors: VisitorEntry[]) {
  const currentDay = startOfDay(referenceDate);

  return Array.from({ length: 7 }, (_, index) => {
    const day = new Date(currentDay);
    day.setDate(currentDay.getDate() - (6 - index));

    const dayVisitors = visitors.filter((visitor) => isSameDay(new Date(visitor.createdAt), day));
    const bookings = dayVisitors.length;
    const revenue = dayVisitors.reduce((sum, visitor) => sum + visitor.totalAmount, 0);

    return {
      day: formatWeekDay(day),
      date: day.toISOString(),
      bookings,
      revenue,
    };
  });
}

function buildCalendarData(referenceDate: Date, visitors: VisitorEntry[]) {
  const currentMonthVisitors = visitors.filter((visitor) => isSameMonth(new Date(visitor.createdAt), referenceDate));
  const bookingDensity: Record<string, number> = {};

  currentMonthVisitors.forEach((visitor) => {
    const day = new Date(visitor.createdAt).getDate();
    bookingDensity[String(day)] = (bookingDensity[String(day)] ?? 0) + 1;
  });

  const firstOfMonth = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), 1);
  const daysInMonth = new Date(referenceDate.getFullYear(), referenceDate.getMonth() + 1, 0).getDate();
  const startOffset = (firstOfMonth.getDay() + 6) % 7;
  const totalCells = Math.ceil((startOffset + daysInMonth) / 7) * 7;

  const cells = Array.from({ length: totalCells }, (_, index) => {
    const day = index - startOffset + 1;
    return day > 0 && day <= daysInMonth ? day : null;
  });

  return {
    cells,
    booking_density: bookingDensity,
    highlighted_day: isSameMonth(referenceDate, firstOfMonth) ? referenceDate.getDate() : null,
  };
}

function buildServiceMix(services: ServiceCatalogEntry[], visitors: VisitorEntry[]) {
  const usageByName = new Map<string, number>();

  visitors.forEach((visitor) => {
    visitor.services.forEach((service) => {
      const name = service.trim();
      if (!name) {
        return;
      }

      usageByName.set(name, (usageByName.get(name) ?? 0) + 1);
    });
  });

  const catalogNames = new Set(services.map((service) => service.name));
  const serviceNames = [...catalogNames, ...Array.from(usageByName.keys()).filter((name) => !catalogNames.has(name))];

  const rows = serviceNames.map((name, index) => ({
    name,
    count: usageByName.get(name) ?? 0,
    color: SERVICE_PALETTE[index % SERVICE_PALETTE.length],
  }));

  if (rows.length === 0) {
    return [
      {
        name: 'Operations',
        count: 1,
        share: 100,
        color: SERVICE_PALETTE[0],
      },
    ];
  }

  const hasUsage = rows.some((row) => row.count > 0);
  const normalizedRows = hasUsage ? rows : rows.map((row) => ({ ...row, count: 1 }));
  const totalCount = normalizedRows.reduce((sum, row) => sum + row.count, 0);

  return normalizedRows
    .map((row) => ({
      name: row.name,
      count: row.count,
      share: totalCount > 0 ? Math.round((row.count / totalCount) * 100) : 0,
      color: row.color,
    }))
    .sort((left, right) => right.count - left.count)
    .slice(0, 5);
}

function buildStaffPerformance(
  staffMembers: StaffMember[],
  visitors: VisitorEntry[],
  sessions: AttendanceSession[],
  referenceDate: Date
) {
  const visitorStatusById = new Map(
    visitors.map((visitor) => [visitor.id, deriveCustomerStatus(visitor, referenceDate)] as const)
  );

  const initialRows = staffMembers.map((staff, index) => {
    const assignedVisitors = visitors.filter((visitor) => visitor.staffId === staff.id);
    const assignedSessions = sessions.filter((session) => session.staffId === staff.id);
    const totalRevenue = assignedVisitors.reduce((sum, visitor) => sum + visitor.totalAmount, 0);
    const ratings = assignedVisitors
      .map((visitor) => visitor.feedbackRating)
      .filter((rating): rating is number => typeof rating === 'number' && rating > 0);
    const followUps = assignedVisitors.filter((visitor) => visitorStatusById.get(visitor.id) === 'follow_up');
    const completedSessions = assignedSessions.filter((session) => session.clockOutAt !== null).length;

    return {
      id: staff.id,
      name: staff.name,
      role: staff.role,
      bookings: assignedVisitors.length,
      revenue: totalRevenue,
      retention: assignedVisitors.length > 0 ? Math.round((followUps.length / assignedVisitors.length) * 100) : staff.retention,
      on_time: assignedSessions.length > 0 ? Math.round((completedSessions / assignedSessions.length) * 100) : staff.onTime,
      rating: ratings.length > 0 ? Math.round(average(ratings) * 10) / 10 : staff.rating,
      utilization: 0,
      color: staff.color,
      index,
    } satisfies HomeStaffPerformanceRecord & { index: number };
  });

  const peakBookings = Math.max(1, ...initialRows.map((row) => row.bookings));

  return initialRows
    .map(({ index, ...row }) => ({
      ...row,
      utilization: row.bookings > 0 ? Math.round((row.bookings / peakBookings) * 100) : 0,
    }))
    .sort((left, right) => right.bookings - left.bookings);
}

export function buildHomeDashboardData({
  staffMembers,
  services,
  visitors,
  sessions,
  referenceDate = new Date(),
}: {
  staffMembers: StaffMember[];
  services: ServiceCatalogEntry[];
  visitors: VisitorEntry[];
  sessions: AttendanceSession[];
  referenceDate?: Date;
}): HomeDashboardData {
  const customerSummaries = buildCustomerSummaries(visitors, referenceDate);
  const weeklyTrend = buildWeeklyTrend(referenceDate, visitors);
  const calendar = buildCalendarData(referenceDate, visitors);
  const serviceMix = buildServiceMix(services, visitors);
  const staffPerformance = buildStaffPerformance(staffMembers, visitors, sessions, referenceDate);

  const weeklyBookings = weeklyTrend.reduce((sum, entry) => sum + entry.bookings, 0);
  const weeklyRevenue = weeklyTrend.reduce((sum, entry) => sum + entry.revenue, 0);
  const overallRevenue = visitors.reduce((sum, visitor) => sum + visitor.totalAmount, 0);
  const averageTicket = weeklyBookings > 0 ? Math.round(weeklyRevenue / weeklyBookings) : 0;
  const feedbackRatings = visitors
    .map((visitor) => visitor.feedbackRating)
    .filter((rating): rating is number => typeof rating === 'number' && rating > 0);
  const reviewedVisitors = visitors.filter(
    (visitor) =>
      visitor.feedbackRespondedAt ||
      (typeof visitor.feedbackRating === 'number' && visitor.feedbackRating > 0) ||
      Boolean(visitor.feedbackNote?.trim())
  );
  const retainedCustomers = customerSummaries.filter((customer) => customer.status === 'follow_up').length;
  const averageStaffRating = average(staffPerformance.map((staff) => staff.rating));
  const averageStaffRetention = average(staffPerformance.map((staff) => staff.retention));
  const averageStaffOnTime = average(staffPerformance.map((staff) => staff.on_time));
  const ratingScore = averageStaffRating > 0 ? (averageStaffRating / 5) * 100 : 0;
  const teamScore = Math.round((ratingScore + averageStaffRetention + averageStaffOnTime) / 3);

  return {
    metrics: {
      weekly_bookings: weeklyBookings,
      weekly_revenue: weeklyRevenue,
      retention: customerSummaries.length > 0 ? Math.round((retainedCustomers / customerSummaries.length) * 100) : 0,
      rating: feedbackRatings.length > 0 ? Math.round(average(feedbackRatings) * 10) / 10 : 0,
      average_ticket: averageTicket,
      team_score: teamScore,
    },
    overview: {
      total_clients: customerSummaries.length,
      total_appointments: visitors.length,
      total_revenue: overallRevenue,
      feedback_count: reviewedVisitors.length,
    },
    weekly_trend: weeklyTrend,
    calendar,
    service_mix: serviceMix,
    staff_performance: staffPerformance,
    clients: customerSummaries.map((customer, index) => ({
      id: customer.key,
      name: customer.name,
      number: customer.phone,
      total_visits: customer.totalVisits,
      first_visit: customer.firstVisitAt,
      last_visit: customer.latestVisitAt,
      appointment_count: customer.totalVisits,
      total_spent: customer.totalSpent,
      latest_services: customer.latestServices,
      latest_staff_name: customer.latestVisit.staffName,
      latest_rating:
        typeof customer.latestVisit.feedbackRating === 'number' ? customer.latestVisit.feedbackRating : null,
      average_rating:
        customer.reviews.length > 0
          ? Math.round(
              average(
                customer.reviews
                  .map((review) => review.rating)
                  .filter((rating): rating is number => typeof rating === 'number' && rating > 0)
              ) * 10
            ) / 10
          : undefined,
      key: customer.key ?? `client-${index}`,
    })),
    appointments: visitors.map((visitor) => ({
      id: visitor.id,
      client_id: visitor.id,
      staff_id: visitor.staffId,
      date: visitor.createdAt,
      status:
        visitor.feedbackDeliveryStatus === 'failed'
          ? 'no_show'
          : visitor.feedbackRespondedAt || (typeof visitor.feedbackRating === 'number' && visitor.feedbackRating > 0)
            ? 'completed'
            : 'confirmed',
      services: visitor.services,
      feedback_flag: Boolean(
        visitor.feedbackRespondedAt ||
          (typeof visitor.feedbackRating === 'number' && visitor.feedbackRating > 0) ||
          Boolean(visitor.feedbackNote?.trim())
      ),
      total_amount: visitor.totalAmount,
    })),
    feedback: reviewedVisitors.map((visitor) => ({
      id: `${visitor.id}-feedback`,
      appointment_id: visitor.id,
      feedback: visitor.feedbackNote?.trim() ?? '',
      rating: typeof visitor.feedbackRating === 'number' ? visitor.feedbackRating : 0,
      comment: visitor.feedbackNote?.trim() ?? '',
    })),
    today_appointments: visitors
      .filter((visitor) => isSameDay(new Date(visitor.createdAt), referenceDate))
      .sort((left, right) => new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime())
      .map((visitor) => ({
        id: visitor.id,
        time: formatTime(new Date(visitor.createdAt)),
        client_name: visitor.visitorName,
        staff_name: visitor.staffName,
        services: visitor.services,
        status:
          visitor.feedbackDeliveryStatus === 'failed'
            ? 'no_show'
            : visitor.feedbackRespondedAt || (typeof visitor.feedbackRating === 'number' && visitor.feedbackRating > 0)
              ? 'completed'
              : 'checked_in',
        total_amount: visitor.totalAmount,
        feedback_flag: Boolean(
          visitor.feedbackRespondedAt ||
            (typeof visitor.feedbackRating === 'number' && visitor.feedbackRating > 0) ||
            Boolean(visitor.feedbackNote?.trim())
        ),
        rating: typeof visitor.feedbackRating === 'number' ? visitor.feedbackRating : null,
      })),
    month_label: formatMonthLabel(referenceDate),
    today_label: new Intl.DateTimeFormat('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
    }).format(referenceDate),
    service: serviceMix[0]?.name ?? services[0]?.name ?? 'Operations',
    version: 'local',
    apiVersion: 'local',
  };
}
